/* eslint-disable react/jsx-no-undef */
/* eslint-disable no-script-url,jsx-a11y/anchor-is-valid */

import { Table, Form, Row, Col, Button, Pagination } from "react-bootstrap";
import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useReducer,
} from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";

import SVG from "react-inlinesvg";
import {
  fetchLockList,
  fetchLockTimeline,
} from "../../../_redux/franchises/franchisesActions";
import { initIds } from "../../../../../../_metronic/_partials/controls/RightPanel";
import { toAbsoluteUrl } from "../../../../../../_metronic/_helpers";
import { useIntl } from "react-intl";
import { CircularProgress } from "@mui/material";
import LockModal from "../franchise-modal/LockModal";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import BatteryGauge from "react-battery-gauge";
import { Feeds } from "../../../../../../_metronic/_partials/widgets";
import { SortableHeader } from "./SortableHeader";
import { NO_SORT, nextSort, sortParams } from "./tableSort";

// Constants
const INITIAL_FILTERS = {
  display_name: "",
  lock_type: "",
  subtype: "",
  serial_number: "",
};

const INITIAL_PAGINATION = {
  currentPage: 1,
  pageSize: 10,
};

const PAGE_SIZE_OPTIONS = [5, 10, 20, 50];
const DEBOUNCE_DELAY = 500;
const INIT_IDS = ["kt_container_panel"];

// Battery gauge customization
const BATTERY_CUSTOMIZATION = {
  batteryBody: {
    strokeColor: "#008000",
    strokeWidth: 3,
  },
  batteryCap: {
    strokeColor: "#008000",
    strokeWidth: 3,
    capToBodyRatio: 0.4,
  },
  batteryMeter: {
    lowBatteryValue: 15,
    color: "#008000",
    lowBatteryColor: "red",
    outerGap: 1,
    noOfCells: 1,
  },
  readingText: {
    lightContrastColor: "#008000",
    darkContrastColor: "#008000",
    display: false,
  },
};

// Reducer for timeline state
const timelineReducer = (state, action) => {
  switch (action.type) {
    case "RESET":
      return {
        title: "",
        lists: [],
        show: false,
      };
    case "SET_TITLE":
      return {
        ...state,
        title: action.payload,
      };
    case "SET_LISTS":
      return {
        ...state,
        lists: action.payload,
      };
    case "SET_SHOW":
      return {
        ...state,
        show: action.payload,
      };
    case "LOAD_TIMELINE":
      return {
        title: action.payload.title,
        lists: [],
        show: true,
        currentLockId: action.payload.lockId,
      };
    default:
      return state;
  }
};

// Custom hook for pagination logic
const usePagination = (totalCount, pageSize, currentPage) => {
  return useMemo(() => {
    const totalPages = Math.ceil(totalCount / pageSize);
    const startIndex = (currentPage - 1) * pageSize + 1;
    const endIndex = Math.min(currentPage * pageSize, totalCount);

    return {
      totalPages,
      startIndex,
      endIndex,
    };
  }, [totalCount, pageSize, currentPage]);
};

// Custom hook for filter logic
/**
 * The lock table's filter inputs, plus a debounced copy of them so typing
 * does not fire a request per keystroke.
 *
 * @returns {{filters: Object, debouncedFilters: Object, setFilters: Function}}
 */
const useFilters = () => {
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [debouncedFilters, setDebouncedFilters] = useState(INITIAL_FILTERS);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilters(filters);
    }, DEBOUNCE_DELAY);

    return () => clearTimeout(timer);
  }, [filters]);

  const handleFilterChange = useCallback((field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters(INITIAL_FILTERS);
    setDebouncedFilters(INITIAL_FILTERS);
  }, []);

  const hasActiveFilters = useMemo(() => {
    return Object.values(filters).some((value) => value.trim() !== "");
  }, [filters]);

  return {
    filters,
    debouncedFilters,
    handleFilterChange,
    clearFilters,
    hasActiveFilters,
  };
};

// Memoized battery level component
const BatteryLevel = React.memo(({ level }) => {
  const batteryLevel = useMemo(() => {
    return level ? level.replace("%", "") : 0;
  }, [level]);
  const isNumber = useMemo(() => {
    return !isNaN(batteryLevel);
  }, [batteryLevel]);

  return isNumber ? (
    <span style={{ display: "flex", alignItems: "center", gap: "10px" }}>
      <BatteryGauge
        maxValue={100}
        orientation="horizontal"
        size={20}
        padding={0}
        value={batteryLevel}
        customization={BATTERY_CUSTOMIZATION}
      />
      {level}
    </span>
  ) : (
    <span>N/A</span>
  );
});

// Memoized status badge component
const StatusBadge = React.memo(({ status }) => {
  const isActive = status === "1";
  const statusClass = isActive ? "success" : "danger";
  const statusText = isActive ? "Active" : "Inactive";

  return (
    <span className={`label label-lg label-light-${statusClass} label-inline`}>
      {statusText}
    </span>
  );
});

// Memoized action buttons component
const ActionButtons = React.memo(
  ({ data, index, onEdit, onTimeline, isEditing }) => {
    return (
      <td className="d-flex">
        <span
          className={`symbol-label ml-2 ${isEditing ? "disabled" : "pointer"}`}
          id={`kt_editlock_panel${index}_toggle`}
          onClick={() => !isEditing && onEdit(data)}
          style={{ opacity: isEditing ? 0.5 : 1 }}
        >
          <span className="svg-icon svg-icon-sm svg-icon-primary">
            {isEditing ? (
              <span
                className="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              ></span>
            ) : (
              <SVG
                className="h-25 align-self-end"
                title="Edit"
                src={toAbsoluteUrl("/media/svg/icons/Design/Edit.svg")}
              />
            )}
          </span>
        </span>
        <span
          className="symbol-label ml-2 pointer"
          id={`kt_locktimeline_panel${index}_timeline`}
          onClick={() => onTimeline(data)}
        >
          <span className="svg-icon svg-icon-sm svg-icon-primary">
            <SVG
              className="h-25 align-self-end"
              title="Timeline"
              src={toAbsoluteUrl("/media/svg/icons/Code/Time-schedule.svg")}
            />
          </span>
        </span>
      </td>
    );
  }
);

// Memoized filter form component
const FilterForm = React.memo(
  ({ filters, onFilterChange, onClearFilters, hasActiveFilters, loading }) => {
    const filterFields = [
      {
        key: "display_name",
        label: "Lock Name",
        placeholder: "Search by lock name",
      },
      {
        key: "lock_type",
        label: "Lock Manufacturer",
        placeholder: "Search by manufacturer",
      },
      {
        key: "subtype",
        label: "Lock Type",
        placeholder: "Search by lock type",
      },
      {
        key: "serial_number",
        label: "Serial Number",
        placeholder: "Search by serial number",
      },
    ];

    return (
      <div className="mt-4 mb-4">
        <div className="card card-custom">
          <div className="card-header">
            <h3 className="card-title">
              <span className="font-weight-bolder text-dark">Filters</span>
            </h3>
          </div>
          <div className="card-body">
            <Form>
              <Row>
                {filterFields.map(({ key, label, placeholder }) => (
                  <Col md={2} key={key}>
                    <Form.Group>
                      <Form.Label className="font-weight-bold">
                        {label}
                      </Form.Label>
                      <Form.Control
                        type="text"
                        placeholder={placeholder}
                        value={filters[key]}
                        onChange={(e) => onFilterChange(key, e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                ))}
                <Col
                  style={{ display: "flex", alignItems: "center", gap: "10px" }}
                >
                  {loading ? (
                    <div className="text-center">
                      <CircularProgress />
                    </div>
                  ) : (
                    <Button
                      variant="primary"
                      size="md"
                      disabled={!hasActiveFilters}
                      onClick={onClearFilters}
                    >
                      Clear Filters
                    </Button>
                  )}
                </Col>
              </Row>
            </Form>
          </div>
        </div>
      </div>
    );
  }
);

// Memoized pagination component
const PaginationControls = React.memo(
  ({
    currentPage,
    totalPages,
    pageSize,
    startIndex,
    endIndex,
    totalCount,
    onPageChange,
    onPageSizeChange,
  }) => {
    const getPageNumbers = useMemo(() => {
      const pages = [];
      const maxVisible = 5;

      if (totalPages <= maxVisible) {
        for (let i = 1; i <= totalPages; i++) {
          pages.push(i);
        }
      } else if (currentPage <= 3) {
        for (let i = 1; i <= maxVisible; i++) {
          pages.push(i);
        }
      } else if (currentPage >= totalPages - 2) {
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        for (let i = currentPage - 2; i <= currentPage + 2; i++) {
          pages.push(i);
        }
      }

      return pages;
    }, [currentPage, totalPages]);

    return (
      <>
        <div className="d-flex justify-content-between align-items-center mb-3 p-3 bg-light rounded">
          <div className="d-flex align-items-center">
            <span className="mr-2 font-weight-bold">Show:</span>
            <Form.Control
              as="select"
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              style={{ width: "auto" }}
              className="form-control-sm"
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </Form.Control>
            <span className="ml-2">entries</span>
          </div>
          <div className="text-muted">
            Showing <strong>{startIndex}</strong> to <strong>{endIndex}</strong>{" "}
            of <strong>{totalCount}</strong> entries
          </div>
        </div>

        {totalPages > 1 && (
          <div className="d-flex justify-content-center mt-4">
            <Pagination className="mb-0">
              <Pagination.First
                onClick={() => onPageChange(1)}
                disabled={currentPage === 1}
              />
              <Pagination.Prev
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
              />

              {getPageNumbers.map((pageNum) => (
                <Pagination.Item
                  key={pageNum}
                  active={pageNum === currentPage}
                  onClick={() => onPageChange(pageNum)}
                >
                  {pageNum}
                </Pagination.Item>
              ))}

              <Pagination.Next
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              />
              <Pagination.Last
                onClick={() => onPageChange(totalPages)}
                disabled={currentPage === totalPages}
              />
            </Pagination>
          </div>
        )}
      </>
    );
  }
);

/**
 * The Locks section of a franchise's details page: filters, a paginated,
 * sortable table (by Lock Name) and each lock's timeline.
 *
 * @param {Object} props
 * @param {Object} props.permission - The viewer's permissions
 * @param {Object} props.franchData - The franchise being viewed
 * @param {string|number} props.fraId - Franchise id
 * @returns {JSX.Element}
 */
export function Lock({ permission, franchData, fraId }) {
  const intl = useIntl();
  const dispatch = useDispatch();

  // State management
  const [dialog, setDialog] = useState(false);
  const [toggleCheck, setToggleCheck] = useState(false);
  const [pagination, setPagination] = useState(INITIAL_PAGINATION);
  const [editingLockId, setEditingLockId] = useState(null);
  const [sort, setSort] = useState(NO_SORT);

  // Timeline state with reducer
  const [timelineState, timelineDispatch] = useReducer(timelineReducer, {
    title: "",
    lists: [],
    show: false,
    currentLockId: null,
  });

  // Custom hooks
  const {
    filters,
    debouncedFilters,
    handleFilterChange,
    clearFilters,
    hasActiveFilters,
  } = useFilters();
  const { currentPage, pageSize } = pagination;

  // Redux state
  const {
    lockList,
    lockLoading,
    lockTotalCount,
    lockTimeline,
    lockTimelineLoading,
  } = useSelector(
    (state) => ({
      lockList: state.franchise.lockList,
      lockLoading: state.franchise.lockLoading,
      lockTotalCount: state.franchise.lockTotalCount || 0,
      lockTimeline: state.franchise.lockTimeline,
      lockTimelineLoading: state.franchise.lockTimelineLoading,
    }),
    shallowEqual
  );

  // Pagination calculations
  const { totalPages, startIndex, endIndex } = usePagination(
    lockTotalCount,
    pageSize,
    currentPage
  );

  // Memoized query params: page, page size, franchise, filters and the column sort.
  const queryParams = useMemo(
    () => ({
      pageNumber: currentPage,
      pageSize: pageSize,
      franchise_id: fraId,
      filter: debouncedFilters,
      ...sortParams(sort),
    }),
    [currentPage, pageSize, fraId, debouncedFilters, sort]
  );

  // Effects
  useEffect(() => {
    if (dialog) initIds(INIT_IDS);
  }, [dialog]);

  useEffect(() => {
    if (toggleCheck) {
      dispatch(fetchLockList(queryParams));
      timelineDispatch({ type: "RESET" });
    }
  }, [toggleCheck, dispatch, queryParams]);

  useEffect(() => {
    if (lockTimeline && lockTimeline.length > 0) {
      const timelineList = lockTimeline.map((data) => ({
        ...data,
        created_at: data.timestamp,
        description: data.value,
        type: "lock",
        title: data.title,
      }));
      timelineDispatch({ type: "SET_LISTS", payload: timelineList });
    }
  }, [lockTimeline]);

  // Event handlers
  const handlePageChange = useCallback((page) => {
    setPagination((prev) => ({ ...prev, currentPage: page }));
    timelineDispatch({ type: "RESET" });
  }, []);

  const handlePageSizeChange = useCallback((size) => {
    setPagination((prev) => ({ currentPage: 1, pageSize: size }));
    timelineDispatch({ type: "RESET" });
  }, []);

  const handleFilterChangeWithReset = useCallback(
    (field, value) => {
      handleFilterChange(field, value);
      setPagination((prev) => ({ ...prev, currentPage: 1 }));
      timelineDispatch({ type: "RESET" });
    },
    [handleFilterChange]
  );

  /** Toggle the sort on a column and go back to the first page. */
  const handleSort = useCallback((field) => {
    setSort((prev) => nextSort(prev, field));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    timelineDispatch({ type: "RESET" });
  }, []);

  const handleClearFiltersWithReset = useCallback(() => {
    clearFilters();
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    timelineDispatch({ type: "RESET" });
  }, [clearFilters]);

  const handleEdit = useCallback((data) => {
    setEditingLockId(data.id);
    setDialog({ check: true, data });
  }, []);

  const handleModalClose = useCallback(() => {
    setEditingLockId(null);
    setDialog(false);
  }, []);

  const handleLoadTimeline = useCallback(
    (data) => {
      timelineDispatch({ type: "RESET" });
      dispatch(fetchLockTimeline(data.id));
      timelineDispatch({
        type: "LOAD_TIMELINE",
        payload: {
          title: `Lock Timeline - ${data.display_name}`,
          lockId: data.id,
        },
      });
    },
    [dispatch]
  );

  // Callback to refresh timeline when lock is edited
  const handleLockEditSuccess = useCallback(() => {
    // Clear editing state
    setEditingLockId(null);

    // Refresh lock list
    dispatch(fetchLockList(queryParams));

    // If timeline is open for the edited lock, refresh it
    if (timelineState.show && timelineState.currentLockId) {
      // Update timeline title with latest lock info
      const updatedLock = lockList.find(
        (lock) => lock.id === timelineState.currentLockId
      );
      if (updatedLock) {
        timelineDispatch({
          type: "LOAD_TIMELINE",
          payload: {
            title: `Lock Timeline - ${updatedLock.display_name}`,
            lockId: timelineState.currentLockId,
          },
        });
      }
      dispatch(fetchLockTimeline(timelineState.currentLockId));
    }
  }, [
    dispatch,
    queryParams,
    timelineState.show,
    timelineState.currentLockId,
    lockList,
  ]);

  const handleToggleCheck = useCallback(() => {
    setToggleCheck((prev) => !prev);
  }, []);

  // Memoized table rows
  const tableRows = useMemo(() => {
    if (!lockList || !lockList.length) return null;

    return lockList.map((data, index) => (
      <tr
        key={index}
        className={editingLockId === data.id ? "table-warning" : ""}
      >
        <td>{data.display_name}</td>
        <td>{data.lock_type}</td>
        <td>{data.subtype}</td>
        <td>{data.serial_number}</td>
        <td>
          <BatteryLevel level={data.battery_level} />
        </td>
        <td>
          <StatusBadge status={data.status} />
        </td>
        <td>{data.site_name}</td>
        <ActionButtons
          data={data}
          index={index}
          onEdit={handleEdit}
          onTimeline={handleLoadTimeline}
          isEditing={editingLockId === data.id}
        />
      </tr>
    ));
  }, [lockList, handleEdit, handleLoadTimeline, editingLockId]);

  return (
    <>
      <div className="card card-custom gutter-b mb-10">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center w-100">
            <h3 className="card-title p-0 m-0">
              <span className="font-weight-bolder text-dark">Lock</span>
              <span>
                {toggleCheck ? (
                  <ExpandLessIcon
                    style={{
                      fontSize: "3rem",
                      margin: " 0 10px",
                      cursor: "pointer",
                    }}
                    onClick={handleToggleCheck}
                  />
                ) : (
                  <KeyboardArrowRightIcon
                    style={{
                      fontSize: "3rem",
                      margin: " 0 10px",
                      cursor: "pointer",
                    }}
                    onClick={handleToggleCheck}
                  />
                )}
              </span>
            </h3>
          </div>

          {toggleCheck && (
            <>
              <FilterForm
                filters={filters}
                onFilterChange={handleFilterChangeWithReset}
                onClearFilters={handleClearFiltersWithReset}
                hasActiveFilters={hasActiveFilters}
                loading={lockLoading}
              />

              {lockList && lockList.length ? (
                <>
                  <Table responsive className="mt-3">
                    <thead>
                      <tr>
                        <SortableHeader
                          label="Lock Name"
                          field="display_name"
                          sort={sort}
                          onSort={handleSort}
                        />
                        <th>Lock Manufacturer</th>
                        <th>Lock Type</th>
                        <th>Serial Number</th>
                        <th>Battery Level</th>
                        <th>Status</th>
                        <th>Site</th>
                        <th style={{ width: "20px" }}></th>
                      </tr>
                    </thead>
                    <tbody>{tableRows}</tbody>
                  </Table>

                  <PaginationControls
                    currentPage={currentPage}
                    totalPages={totalPages}
                    pageSize={pageSize}
                    startIndex={startIndex}
                    endIndex={endIndex}
                    totalCount={lockTotalCount}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                  />
                </>
              ) : (
                <>
                  {lockLoading ? (
                    <div className="text-center mt-8">
                      <CircularProgress />
                    </div>
                  ) : (
                    <div className="mt-4" style={{ textAlign: "center" }}>
                      <p>{intl.formatMessage({ id: "No.Record.Found" })}</p>
                    </div>
                  )}
                </>
              )}

              {/* Timeline */}
              {lockTimelineLoading ? (
                <div className="text-center">
                  <CircularProgress />
                  <p className="text-muted mt-2">Loading timeline...</p>
                </div>
              ) : (
                <>
                  {toggleCheck && timelineState.show && (
                    <div>
                      <Feeds
                        classNameSend="card-stretchs"
                        data={timelineState.lists}
                        type={"lock"}
                        title={timelineState.title}
                      />
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>

      {dialog && (
        <LockModal
          dialog={dialog}
          setDialog={handleModalClose}
          edit="edit"
          fraId={fraId}
          currentQueryParams={queryParams}
          onLockEditSuccess={handleLockEditSuccess}
        />
      )}
    </>
  );
}
